import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { SIN_DATO } from "@/components/ui/empty-value";

/**
 * Ficha de detalle del estándar interno: etiqueta chica en mayúsculas y el
 * valor debajo, en rejilla de dos columnas y agrupado por secciones.
 *
 * Nació escrita a mano dentro de `legal-flow/CompradorDetalleSheet` (`DrawerKv`)
 * y es la forma que ya se aprobó para leer un expediente: texto directo,
 * seccionado, sin una tarjeta por dato. Un recuadro por campo pesa más que el
 * campo y obliga a leer en zigzag; aquí el recuadro solo aparece cuando el
 * bloque de verdad es una unidad aparte.
 *
 * Vive en `ui/` para que cualquier portal (bancos, cobranza, notaría, socio
 * bancario) arme sus modales de detalle igual, en vez de volver a copiar clases.
 */

export interface DetailFieldProps {
  label: string;
  /** `null`/vacío se dice con palabras (`vacio`), no con un guion. */
  value?: ReactNode;
  /** Monoespaciado para RFC, CURP, CLABE, folios. */
  mono?: boolean;
  /** Números que se comparan en columna (montos, metros, porcentajes). */
  tabular?: boolean;
  /** Texto para el vacío: "Sin RFC", "Sin domicilio capturado". */
  vacio?: string;
  /** Ocupa las dos columnas de la rejilla. */
  ancho?: boolean;
  className?: string;
}

export function DetailField({
  label,
  value,
  mono = false,
  tabular = false,
  vacio,
  ancho = false,
  className,
}: DetailFieldProps) {
  const limpio = typeof value === "string" ? value.trim() : value;
  const hayDato =
    limpio != null && limpio !== "" && limpio !== "-" && limpio !== "—";

  return (
    <div className={cn(ancho && "col-span-2", className)}>
      <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
        {label}
      </p>
      <div
        className={cn(
          "text-[13px] break-words",
          mono && hayDato && "font-mono",
          tabular && hayDato && "tabular-nums",
          hayDato ? "text-foreground" : "text-muted-foreground/70",
        )}
      >
        {hayDato ? limpio : (vacio ?? SIN_DATO)}
      </div>
    </div>
  );
}

/** Rejilla de campos: dos columnas, una sola en pantalla chica. */
export function DetailGrid({
  children,
  columnas = 2,
  className,
}: {
  children: ReactNode;
  columnas?: 1 | 2 | 3;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid gap-x-4 gap-y-3",
        columnas === 1 && "grid-cols-1",
        columnas === 2 && "grid-cols-1 sm:grid-cols-2",
        columnas === 3 && "grid-cols-2 sm:grid-cols-3",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Sección del detalle: título chico con línea y, debajo, sus campos. Sustituye
 * a envolver cada grupo en un `Card`.
 *
 * Con `icon` (Mi cuenta, 2026-10-28, aprobado por Eduardo): el título pasa a texto normal con el
 * ícono en un cuadro de color tenue y los campos quedan alineados bajo el título. Las secciones se
 * separan con aire (`space-y-8` del contenedor), sin líneas ni tarjetas: el ícono marca dónde
 * empieza cada una. Es la forma a usar en detalles nuevos.
 */
export function DetailSection({
  title,
  action,
  children,
  className,
  regla = false,
  icon: Icon,
  description,
  sangria = true,
}: {
  title: string;
  /** Acción al ras del título (ver documento, abrir panel). */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  /**
   * Línea bajo el título. Apagada por defecto: con cuatro secciones seguidas,
   * cuatro reglas horizontales cortan el barrido vertical en vez de guiarlo.
   * El aire entre secciones ya las separa.
   */
  regla?: boolean;
  /** Ícono de la sección: activa el encabezado con ícono (ver arriba). */
  icon?: LucideIcon;
  /** Texto corto bajo el título (solo con `icon`). */
  description?: ReactNode;
  /**
   * Campos alineados bajo el título (sangría del ancho del ícono, desde `sm`). Apagarla en
   * columnas angostas (ficha lateral de 240 px) donde 38 px más truncan los valores.
   */
  sangria?: boolean;
}) {
  if (Icon) {
    return (
      <section className={cn("space-y-3", className)}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <span aria-hidden className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Icon className="size-4" strokeWidth={1.75} />
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground">{title}</h3>
              {description && <p className="text-[12px] leading-snug text-muted-foreground">{description}</p>}
            </div>
          </div>
          {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
        </div>
        <div className={cn(sangria && "sm:pl-[38px]")}>{children}</div>
      </section>
    );
  }
  return (
    <section className={cn("space-y-2", className)}>
      <div
        className={cn(
          "flex items-center justify-between gap-3",
          regla && "border-b border-border pb-1.5",
        )}
      >
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}
